import React, { useState } from 'react';
import { Bird, Headset, Volume2, VolumeX, Menu, X, Share2, Waves, Eye, EyeOff, Gamepad2 } from 'lucide-react';
import Minimap from './Minimap';
import TourPanel from './TourPanel';
import EnvironmentDeck from './EnvironmentDeck';
import FieldCard from './FieldCard';
import FPSMeter from './FPSMeter';
import EcologyPanel from './EcologyPanel';
import Postcard from './Postcard';
import SideCard from './SideCard';
import VehicleSelector from './VehicleSelector';
import ZoneMenu, { ZoneMenuButton } from './ZoneMenu';
import { useStore } from '../store';
import { soundEngine } from '../audio/soundEngine';

function MuteButton() {
  const audioEnabled = useStore((s) => s.audioEnabled);
  const toggleAudio = useStore((s) => s.toggleAudio);
  const onClick = () => { toggleAudio(); soundEngine.setEnabled(!audioEnabled); soundEngine.start(); };
  return (
    <button
      data-testid="mute-btn"
      onClick={onClick}
      title={audioEnabled ? 'Mute' : 'Unmute'}
      className={`pointer-events-auto flex items-center justify-center w-[53px] h-[53px] rounded-full border-2 border-gold transition-colors duration-300 ${
        audioEnabled ? 'bg-gold text-black' : 'bg-black/70 text-white'
      }`}
    >
      {audioEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
    </button>
  );
}

// Harpy Eagle guide volume — a slider so listeners can tuck the briefings under
// the ambience instead of only on/off. Forest + Outrage-Storm audio are unaffected.
function GuideVolumeSlider() {
  const guideVolume = useStore((s) => s.guideVolume);
  const setGuideVolume = useStore((s) => s.setGuideVolume);
  const onChange = (e) => { const v = parseFloat(e.target.value); setGuideVolume(v); soundEngine.setNarrationVolume(v); soundEngine.start(); };
  const muted = guideVolume < 0.01;
  return (
    <div data-testid="guide-volume" className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full glass" title="Harpy Eagle guide volume">
      {muted ? <VolumeX className="w-4 h-4 text-white/55" strokeWidth={1.8} /> : <Bird className="w-4 h-4 text-gold" strokeWidth={1.6} />}
      <span className="font-mono text-[9px] uppercase tracking-widest text-white/55 hidden xl:inline">Guide</span>
      <input
        data-testid="guide-volume-slider"
        type="range" min="0" max="1" step="0.05" value={guideVolume}
        onChange={onChange}
        className="w-20 accent-gold cursor-pointer"
      />
    </div>
  );
}

// Hide Panels — instantly clears all floating 3D zone labels/placards for a clean jungle view.
function HidePanelsButton() {
  const panelsHidden = useStore((s) => s.panelsHidden);
  const togglePanelsHidden = useStore((s) => s.togglePanelsHidden);
  return (
    <button
      data-testid="hide-panels-btn"
      onClick={togglePanelsHidden}
      title={panelsHidden ? 'Show floating data panels' : 'Hide floating data panels for a clean view'}
      className={`pointer-events-auto flex items-center gap-2 px-3.5 py-2.5 rounded-full transition-colors duration-300 uppercase tracking-widest text-[10px] font-bold ${
        panelsHidden ? 'glass-gold text-gold' : 'glass text-white/70 hover:text-gold hover:border-gold'
      }`}
    >
      {panelsHidden ? <EyeOff className="w-4 h-4" strokeWidth={1.7} /> : <Eye className="w-4 h-4" strokeWidth={1.7} />}
      {panelsHidden ? 'Show' : 'Hide'}
    </button>
  );
}

function ShareButton() {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    const s = useStore.getState();
    const url = `${window.location.origin}/?direct=1&zone=${s.currentZone}&time=${s.timeValue.toFixed(2)}&weather=${s.weather}`;
    try { await navigator.clipboard.writeText(url); } catch (e) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button data-testid="share-btn" onClick={onClick}
      className="pointer-events-auto flex items-center gap-2 px-3.5 py-2.5 glass-gold rounded-full text-gold hover:bg-gold hover:text-black transition-colors duration-300 uppercase tracking-widest text-[10px] font-bold">
      <Share2 className="w-4 h-4" strokeWidth={1.6} />
      {copied ? 'Copied' : 'Share'}
    </button>
  );
}

function NeutralityIndex() {
  const mode = useStore((s) => s.mode);
  if (mode !== 'boat') return null;
  return (
    <div data-testid="neutrality-index" className="absolute top-36 md:top-44 left-1/2 -translate-x-1/2 glass rounded-full px-5 py-2 flex items-center gap-3 pointer-events-none">
      <Waves className="w-4 h-4 text-[#38bdf8]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">Neutrality Index</span>
      <span className="font-mono text-sm font-bold text-[#38bdf8]">100%</span>
    </div>
  );
}

// MUTE GUIDE — quick on/off silence for the eagle's briefings (sits below the master mute).
function MuteGuideButton() {
  const guideVolume = useStore((s) => s.guideVolume);
  const setGuideVolume = useStore((s) => s.setGuideVolume);
  const muted = guideVolume < 0.01;
  const onClick = () => { const v = muted ? 1 : 0; setGuideVolume(v); soundEngine.setNarrationVolume(v); soundEngine.start(); };
  return (
    <button
      data-testid="mute-guide-btn"
      onClick={onClick}
      title={muted ? 'Unmute the Harpy Eagle guide' : 'Mute the Harpy Eagle guide (ambience keeps playing)'}
      className={`pointer-events-auto flex items-center gap-1.5 px-3 h-[38px] rounded-full border-2 transition-colors duration-300 uppercase tracking-[0.15em] text-[9px] font-bold ${
        muted ? 'border-white/25 bg-black/60 text-white/60' : 'border-gold bg-gold/15 text-gold'
      }`}
    >
      <Bird className="w-4 h-4" strokeWidth={1.7} />
      Mute Guide
    </button>
  );
}

// Desktop opt-in for the on-screen dual joysticks (touch devices show them automatically).
function JoystickButton() {
  const joysticks = useStore((s) => s.joysticks);
  const toggleJoysticks = useStore((s) => s.toggleJoysticks);
  return (
    <button
      data-testid="joystick-toggle"
      onClick={toggleJoysticks}
      title="Toggle on-screen joysticks"
      className={`pointer-events-auto hidden lg:flex items-center gap-2 px-3.5 py-2.5 rounded-full transition-colors duration-300 uppercase tracking-widest text-[10px] font-bold ${
        joysticks ? 'glass-gold text-gold' : 'glass text-white/70 hover:text-gold hover:border-gold'
      }`}
    >
      <Gamepad2 className="w-4 h-4" /> Sticks
    </button>
  );
}

export default function HUD({ onEnterVR, vrSupported }) {
  const hudOpen = useStore((s) => s.hudOpen);
  const toggleHud = useStore((s) => s.toggleHud);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 flex flex-col justify-between px-4 pt-3 pb-4 md:p-9 animate-hud-in">
      {/* Top bar */}
      <div className="flex justify-between items-start w-full">
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-2">
            <MuteButton />
            <MuteGuideButton />
            <div className="pointer-events-auto hidden sm:block"><FPSMeter /></div>
          </div>
          <div className="pointer-events-auto block">
            <div className="flex items-center gap-2 sm:gap-2.5 text-gold">
              <img src="/tab-seal.png" alt="TAB seal" className="w-6 h-6 sm:w-7 sm:h-7 object-contain shrink-0" />
              <span className="font-serif text-base sm:text-xl tracking-tight text-white whitespace-nowrap">
                The Jungle <span className="italic text-gold">of</span> Bias
              </span>
            </div>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 pl-8 hidden sm:block">
              Ideological Ecology · Live Simulation
            </p>
          </div>
        </div>

        {/* Right side = a single FLEX-COLUMN. Controls, compass and Environment
            Deck stack vertically in normal flow, so they can NEVER overlap. */}
        <div className="flex flex-col items-end gap-3">
          {/* Mobile/iPad toggle */}
          <button data-testid="hud-toggle" onClick={toggleHud}
            className="lg:hidden pointer-events-auto flex items-center justify-center w-11 h-11 rounded-full glass text-gold">
            {hudOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div className={`${hudOpen ? 'flex' : 'hidden'} lg:flex flex-col items-end gap-3`}>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <ShareButton />
              <Postcard />
              <HidePanelsButton />
              <JoystickButton />
              <GuideVolumeSlider />
              <ZoneMenuButton />
              {vrSupported && (
                <button
                  data-testid="hud-enter-vr-btn"
                  onClick={onEnterVR}
                  className="p-3 rounded-full glass text-white/80 hover:text-gold hover:border-gold transition-colors duration-300 pointer-events-auto"
                  title="Enter VR"
                >
                  <Headset className="w-4 h-4" strokeWidth={1.6} />
                </button>
              )}
            </div>
            {/* Compass */}
            <div className="scale-[0.7] lg:scale-100 origin-top-right"><Minimap /></div>
            {/* Zone Definition panel — moved to the right, directly under the compass */}
            <TourPanel />
            {/* Environment Deck — stacked below */}
            <EnvironmentDeck />
          </div>

          {/* Persistent compass on phones/tablets when the menu is closed (nav aid) */}
          <div className={`${hudOpen ? 'hidden' : 'block'} lg:hidden scale-[0.7] origin-top-right`}>
            <Minimap />
          </div>
        </div>
      </div>

      {/* Top-CENTER travel controls — Vehicle + Ecology sit below the title and
          clear of the compass on the right. Collapse into the toggle on mobile. */}
      <div className={`${hudOpen ? 'flex' : 'hidden'} lg:flex absolute top-16 sm:top-14 md:top-24 left-1/2 -translate-x-1/2 items-center gap-4 z-[80]`}>
        <VehicleSelector />
        <EcologyPanel variant="deck" />
      </div>

      <NeutralityIndex />

      {/* Field Guide fly-by name card */}
      <FieldCard />

      {/* Zone jump menu (absolute) */}
      <ZoneMenu />

      {/* Zone info side-card (slides in when an Info Pin is tapped) */}
      <SideCard />
    </div>
  );
}
